import { Component } from '@theme/component';

const VALIDATION_MESSAGES = {
  email: 'Please enter a valid email address',
  tel: 'Please enter a valid phone number',
  number: 'Please enter a valid number',
  required: 'This field is required',
};

class CustomFormComponent extends Component {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.formId = this.dataset.formId;

    this.addEventListener('input', this.#onFieldChange);
    this.addEventListener('change', this.#onFieldChange);

    // Prevent form submit when invalid
    const form = document.getElementById(this.formId);
    if (form) {
      form.addEventListener('submit', this.#onSubmit);
    }

    // Watch for variant changes that re-enable the button
    this.#observer = new MutationObserver(() => this.#syncButtonState());

    // Initial validation after DOM is ready
    requestAnimationFrame(() => {
      this.#observeButton();
      this.#syncButtonState();
    });
    document.querySelectorAll('.shopify-payment-button__button').forEach(btn => {
  const observer = new MutationObserver(() => {
    const span = btn.querySelector('[role="button"] span');
    if (span && span.textContent.trim() !== 'Subscribe Now') {
      span.textContent = 'Subscribe Now';
    }
  });
  observer.observe(btn, { childList: true, subtree: true });
});
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();

    const form = document.getElementById(this.formId);
    if (form) {
      form.removeEventListener('submit', this.#onSubmit);
    }
  }

  /** @returns {NodeListOf<HTMLInputElement | HTMLSelectElement>} */
  get fields() {
    return this.querySelectorAll('[data-custom-field]');
  }

  /** @returns {HTMLElement | null} */
  get productFormComponent() {
    const form = document.getElementById(this.formId);
    return form?.closest('product-form-component') ?? null;
  }

  #observeButton() {
    const btn = this.productFormComponent?.querySelector('button[name="add"]');
    if (btn) {
      this.#observer?.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
    }
  }

  #onFieldChange = () => {
    this.#syncButtonState();
  };

  #onSubmit = (/** @type {Event} */ e) => {
    if (!this.#isFormValid()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.#showErrors();
    }
  };

  /** @returns {boolean} */
  #isFormValid() {
    return Array.from(this.fields).every((field) => field.checkValidity());
  }

  #syncButtonState() {
    const pfc = this.productFormComponent;
    if (!pfc) return;

    const valid = this.#isFormValid();

    if (valid) {
      pfc.removeAttribute('data-custom-form-invalid');
    } else {
      pfc.setAttribute('data-custom-form-invalid', '');
    }
  }

  #showErrors() {
    this.fields.forEach((field) => {
      const wrapper = field.closest('.custom-form-field');
      const errorEl = wrapper?.querySelector('.custom-form-field__error');
      if (!errorEl) return;

      if (!field.checkValidity()) {
        field.setAttribute('aria-invalid', 'true');
        const type = field.type || 'text';
        errorEl.textContent = !field.value.trim()
          ? VALIDATION_MESSAGES.required
          : VALIDATION_MESSAGES[type] || field.validationMessage;
      } else {
        field.removeAttribute('aria-invalid');
        errorEl.textContent = '';
      }
    });
  }
}

if (!customElements.get('custom-form-component')) {
  customElements.define('custom-form-component', CustomFormComponent);
}
